import getFirstValidThreadTimestamp from '@/lib/threads/getFirstValidThreadTimestamp.js';
import getOptimisticCarryoverThreads from '@/lib/threads/getOptimisticCarryoverThreads.js';
import getThreadContentItemByThreadId from '@/lib/threads/getThreadContentItemByThreadId.js';
import getThreadId from '@/lib/threads/getThreadId.js';
import getThreadsById from '@/lib/threads/getThreadsById.js';
import inheritThreadProjectMetadataFromParent from '@/lib/threads/inheritThreadProjectMetadataFromParent.js';
import isIncomingThreadMetadataStale from '@/lib/threads/isIncomingThreadMetadataStale.js';
import mergeThreadProjectMetadata from '@/lib/threads/mergeThreadProjectMetadata.js';
import shouldPreserveExistingRunStateDuringHydration from '@/lib/threads/shouldPreserveExistingRunStateDuringHydration.js';
import sortThreadsBySidebarTimestamp from '@/lib/threads/sortThreadsBySidebarTimestamp.js';

const reconcileHydratedThreads = ({ incomingThreads = [], existingThreads = [], existingThreadContent = [], projectMetaById = {}, activeProjectId = null } = {}) => {
  const existingThreadsById = getThreadsById(existingThreads);
  const incomingThreadsWithOptimisticMetadata = incomingThreads.map((thread) => {
    const threadId = getThreadId(thread);

    if (!threadId) {
      return thread;
    }

    const existingThread = existingThreadsById?.[threadId] || {};
    const optimisticCreatedAt = getFirstValidThreadTimestamp([thread?.optimisticCreatedAt, existingThread?.optimisticCreatedAt]);
    const shouldPreserveExistingMetadata = isIncomingThreadMetadataStale({
      existingThread,
      incomingThread: thread
    });
    const createdAt = getFirstValidThreadTimestamp(shouldPreserveExistingMetadata ? [existingThread?.createdAt, existingThread?.created_at, thread?.createdAt, thread?.created_at, existingThread?.updatedAt, existingThread?.updated_at, thread?.updatedAt, thread?.updated_at] : [thread?.createdAt, thread?.created_at, existingThread?.createdAt, existingThread?.created_at, thread?.updatedAt, thread?.updated_at, existingThread?.updatedAt, existingThread?.updated_at]);
    const updatedAt = getFirstValidThreadTimestamp(shouldPreserveExistingMetadata ? [existingThread?.updatedAt, existingThread?.updated_at, thread?.updatedAt, thread?.updated_at, createdAt] : [thread?.updatedAt, thread?.updated_at, existingThread?.updatedAt, existingThread?.updated_at, createdAt]);
    const mergedThread = shouldPreserveExistingMetadata ? { ...thread, ...existingThread } : { ...existingThread, ...thread };

    return {
      ...mergedThread,
      ...(createdAt ? { createdAt } : {}),
      ...(updatedAt ? { updatedAt } : {}),
      ...(optimisticCreatedAt ? { optimisticCreatedAt } : {})
    };
  });
  const optimisticCarryoverThreads = getOptimisticCarryoverThreads({
    incomingThreads: incomingThreadsWithOptimisticMetadata,
    existingThreadsById
  });
  const threadsWithProjectMetadata = mergeThreadProjectMetadata({
    threads: sortThreadsBySidebarTimestamp(incomingThreadsWithOptimisticMetadata.concat(optimisticCarryoverThreads)),
    projectMetaById,
    existingThreadsById,
    activeProjectId
  });
  const hydratedThreads = inheritThreadProjectMetadataFromParent({
    threads: threadsWithProjectMetadata,
    projectMetaById,
    existingThreadsById
  });

  return (hydratedThreads || []).map((thread) => {
    const threadId = getThreadId(thread);

    if (!threadId) {
      return thread;
    }

    const existingRunState = getThreadContentItemByThreadId(existingThreadContent, threadId)?.runState || null;
    const incomingRunState = thread?.runState || null;

    if (
      !shouldPreserveExistingRunStateDuringHydration({
        existingRunState,
        incomingRunState
      })
    ) {
      return thread;
    }

    return {
      ...thread,
      runState: existingRunState
    };
  });
};

export default reconcileHydratedThreads;
